import { defineStore } from "pinia";
import { ref } from "vue";
import type { Attachment } from "@/API";
import {
  convertFileUrl,
  getFileType,
  getThumbnail,
} from "@/components/file-utility";

export type FileParam = {
  domainId: string;
  topicId: string;
  messageId?: string;
  commentId?: string;
  fileName: string;
  data?: string;
  thumb?: string;
};

export type FileSignedUrl = {
  key: string;
  signedUrl: string;
};

export type FileLocalUrl = {
  key: string;
  url: string;
};

export type SetFiles = {
  thumb?: FileLocalUrl[];
  data?: FileLocalUrl[];
};

// fileKeyをparamに分解
const parseKey = (
  key: string,
): {
  domainId: string;
  topicId: string;
  messageId?: string;
  commentId?: string;
  fileName: string;
} => {
  const pathList = key.split("/");
  const domainId = pathList[0];
  const topicId = pathList[1];
  const backPathList = pathList.slice(2);
  if (backPathList.length === 1) {
    // topic
    const fileName = backPathList[0];
    return { domainId, topicId, fileName };
  } else if (backPathList.length === 2) {
    // message
    const messageId = backPathList[0];
    const fileName = backPathList[1];
    return { domainId, topicId, messageId, fileName };
  } else if (backPathList.length === 3) {
    // comment
    const messageId = backPathList[0];
    const commentId = backPathList[1];
    const fileName = backPathList[2];
    return { domainId, topicId, messageId, commentId, fileName };
  } else {
    return { domainId, topicId, fileName: "" };
  }
};

const isPdfOrMovie = (file: File | Attachment): boolean => {
  const type = getFileType(file);
  switch (type) {
    case "pdf":
    case "video":
      return true;
    default:
      return false;
  }
};

export const useFileStore = defineStore("files", () => {
  const files = ref<FileParam[]>([]);
  const thumbStore = ref<{ [fileName: string]: File }>({});

  const getTopicFiles = (domainId: string): FileParam[] | undefined => {
    return files.value.filter(
      (param) =>
        param.domainId === domainId && !param.messageId && !param.commentId,
    );
  };
  const getMessageFiles = (
    domainId: string,
    topicId: string,
  ): FileParam[] | undefined => {
    return files.value.filter(
      (param) =>
        param.domainId === domainId &&
        param.topicId === topicId &&
        param.messageId &&
        !param.commentId,
    );
  };
  const getCommentFilesByTopicId = (
    domainId: string,
    topicId: string,
  ): FileParam[] | undefined => {
    return files.value.filter(
      (param) =>
        param.domainId === domainId &&
        param.topicId === topicId &&
        param.messageId &&
        param.commentId,
    );
  };
  const getCommentFilesByMessageId = (
    domainId: string,
    topicId: string,
    messageId: string,
  ): FileParam[] | undefined => {
    return files.value.filter(
      (param) =>
        param.domainId === domainId &&
        param.topicId === topicId &&
        param.messageId === messageId &&
        param.commentId,
    );
  };
  const getThumbStore = (): File[] => {
    return Object.values(thumbStore.value);
  };
  const getThumb = (file: File): File | undefined => {
    const fileName = `${file.name + file.size}_tmb.jpg`;
    return thumbStore.value[fileName];
  };

  // --- 内部更新 (旧 mutations 相当) ---
  const setDataMutation = (data: SetFiles): void => {
    const keys: ("thumb" | "data")[] = ["thumb", "data"];
    for (const _key of keys) {
      const dataList = data[_key] || undefined;
      if (!dataList) continue;
      for (const _data of dataList) {
        const param = parseKey(_data.key);
        const fileParam: FileParam = {
          ...param,
          data: _key === "data" ? _data.url : undefined,
          thumb: _key === "thumb" ? _data.url : undefined,
        };
        const stateData = files.value.find(
          (p) =>
            p.domainId === fileParam.domainId &&
            p.topicId === fileParam.topicId &&
            p.messageId === fileParam.messageId &&
            p.commentId === fileParam.commentId &&
            p.fileName === fileParam.fileName,
        );
        if (!stateData) {
          files.value.push(fileParam);
        } else {
          if (fileParam.data !== undefined) {
            stateData.data = fileParam.data;
          }
          if (fileParam.thumb !== undefined) {
            stateData.thumb = fileParam.thumb;
          }
        }
      }
    }
  };

  // --- actions ---
  const set = async (payload: {
    thumb?: FileSignedUrl[];
    data?: FileSignedUrl[];
  }): Promise<SetFiles> => {
    const setData: SetFiles = {};
    const data = payload;
    const keys: ("thumb" | "data")[] = ["thumb", "data"];
    for (const _key of keys) {
      const dataList = data[_key] || undefined;
      if (!dataList) continue;
      setData[_key] = await convertFileUrl(dataList);
    }
    setDataMutation(setData);
    return setData;
  };

  const pushThumbStore = async (filesArg: File[]): Promise<void> => {
    const results = await Promise.all(
      filesArg
        .filter((file: File): file is File => isPdfOrMovie(file))
        .map(async (file) => {
          const thumb = await getThumbnail(file);
          return thumb;
        }),
    );
    const resultFiles = results.filter(
      (result: File | undefined): result is File => Boolean(result),
    );
    const thumbUpdates: Record<string, File> = {};
    resultFiles.forEach((file) => {
      thumbUpdates[file.name] = file;
    });
    thumbStore.value = { ...thumbStore.value, ...thumbUpdates };
  };

  return {
    files,
    thumbStore,
    getTopicFiles,
    getMessageFiles,
    getCommentFilesByTopicId,
    getCommentFilesByMessageId,
    getThumbStore,
    getThumb,
    set,
    pushThumbStore,
  };
});

export default useFileStore;
